import { useAuth } from '@agentnet/auth';
import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Container, Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { GetActionLogs } from 'api/dashboards/service-orders';
import { fileInfoType } from 'api/file/interfaces/get-file';
import {
  getOrderDocument,
  postServiceOrder,
  postServiceOrderDocumentDownload,
  putServiceOrder,
} from 'api/serviceorder-api';
import { ServiceOrderDrawer } from 'core/layout-core/core-content/Dashboards/ServiceOrders/ServiceOrderDrawer';
import useAsync from 'hooks/useAsync';
import React, { useEffect, useReducer, useRef, useState } from 'react';
import LoadingSpinner from 'ui-kit/components/LoadingSpinner';
import AccordionContent from 'ui-kit/components/accordion/AccordionContent';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import AgentNetDivider from 'ui-kit/components/dividers/AgentNetDivider2';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import { AgentNetConfirmationDialog } from 'ui-kit/components/modal/ConfirmationDialog';
import NoResults from 'ui-kit/components/noResults/NoResults';
import { Notification } from 'ui-kit/components/notification/Notification';
import { SnackBarMessageType } from 'ui-kit/components/notification/SnackbarProvider';
import useGlobalMessages from 'ui-kit/components/notification/useGlobalMessages';
import useSnackBars from 'ui-kit/components/notification/useSnackbars';
import { AgentNetTextInput } from 'ui-kit/inputs';
import { formatDate } from 'utilities/dateFormatter';
import { openDocument, openURLDocument, sanitizeFileName } from 'utilities/utilities';
import { DocumentType, fpxDocType, KBLinks, newWindow, pxtDocType, WindowFeatures, zipDocType } from '../constants';
import { useFileAmendability } from './service-order-utils';
import EditOrderForm from './EditOrderForm';
import OrderInsightHistory from './OrderInsightHistory';
import ServiceOrderActions from './ServiceOrderActions';
import ServiceOrderForm from './ServiceOrderForm';
import {
  APIStatus,
  noActiveServiceOrders,
  openFile,
  OrderInsights,
  serviceOrderEditMessage,
  ServiceOrderMessage,
  ServiceOrdersActions,
  ServiceOrderStatus,
} from './constants';
import {
  Contact,
  DownloadDocumentRequest,
  DownloadDocumentResponse,
  Field,
  InsightTitleStatus,
  Order,
  OrderDocument,
  OrderInsightsSummary,
  ProductResponse,
  ServiceOrderRequest,
} from './type';

interface ServiceOrderProps {
  serviceOrderList?: Order[] | null;
  fileData?: fileInfoType | null;
  productList?: ProductResponse[] | null;
  productListStatus?: string;
  productListError?: string[];
  decryptedFileId?: number | null;
  isAddNewServiceOrderClicked?: boolean;
  OnCancel?: () => void;
  handleAddNewServiceOrder?: () => void;
  refreshServiceOrderData?: () => void;
}
const ServiceOrderFC: React.FC<ServiceOrderProps> = ({
  serviceOrderList,
  fileData,
  productList,
  productListStatus,
  productListError,
  decryptedFileId,
  isAddNewServiceOrderClicked,
  handleAddNewServiceOrder,
  refreshServiceOrderData,
}: ServiceOrderProps) => {
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      documentsTable: {
        width: '100%',
        padding: theme.spacing(0, 0, 3),
      },
      documentsTableHeader: {
        backgroundColor: '#F2F5F6',
        borderBottom: '1px solid #D9E0E5',
        fontWeight: 600,
        fontSize: pxToRem(14),
      },
      tableHeaderCell: {
        padding: pxToRem(12),
      },
      documentsTableList: {},
      tableRow: {
        borderBottom: `1px solid #E0E0E0`,
      },
      tableCell: {
        padding: pxToRem(12),
        display: 'flex',
        alignItems: 'center',
      },
      root: {
        width: '100%',
        background: theme.palette.common.white,
      },
      serviceOrdersWrap: {
        padding: pxToRem(0, 24, 48),
        marginBottom: theme.spacing(5),
      },
      warningMsg: { marginBottom: theme.spacing(3) },
      mb2: {
        marginBottom: theme.spacing(2),
      },
      contentClass: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        margin: '0 auto',
        width: '80%',
        height: '100%', // Ensure it takes the full height of the parent
      },
      loadingClass: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100%',
        minHeight: 'calc(100vh - 400px)', // Set minimum height to 100% of the viewport height
      },
    }),
  );

  const { getAccessToken } = useAuth();
  const randomId = () => (Math.random() + 1).toString(36).substring(7);
  const classes = useStyles();
  const [draftMode, setDraftMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [orders, setOrders] = useState<Order[]>([]);
  const [initialOrders, setInitialOrders] = useState<Order[]>([]);
  const [activeOrdersCount, setActiveOrdersCount] = useState<number>(0);
  const [documentUploadStatus, setDocumentUploadStatus] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
  const [updateAttempted, setUpdateAttempted] = useState<boolean>(false);

  const { addGlobalMsg } = useGlobalMessages();
  const isFileAmendable = useFileAmendability(fileData?.fileStatus);
  const [errMsgs, setErrMsgs] = useState([]);
  const [displayError, setDisplayError] = useState<boolean>(false);
  const [triggerSaveCall, setTriggerSaveCall] = useState(false);
  const [triggerUpdateCall, setTriggerUpdateCall] = useState(false);
  const [isCreateServiceOrderButtonDisabled, setIsCreateServiceOrderButtonDisabled] = useState(false);
  const [selectedOrderNumber, setselectedOrderNumber] = useState<any>('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [noteDialog, setNoteDialog] = React.useState<any>({ show: false, id: undefined, note: '' });
  const [selectedViewDocument, setSelectedViewDocument] = useState<any>('');
  const [selectedDownloadDocument, setSelectedDownloadDocument] = useState<string[]>([]);
  const [downloadAsPdf, setDownloadAsPdf] = useState<boolean>(false);
  const [auditAndConversationPayload, setAuditAndConversationPayload] = useState<GetActionLogs | undefined>(undefined);

  const [documents, setDocuments] = useState<OrderDocument[]>([]);
  const [orderDetails, setOrderDetails] = useState<any>(null);

  const [update, setUpdate] = useState(false);
  const { addSnackbarMessage, removeSnackbarMessage } = useSnackBars();
  const infoSnackbarId = useRef<string | null>(null);

  const reducer = (state: any, action: any) => {
    switch (action.type) {
      case ServiceOrdersActions.edit:
        return { ...state, isEditing: true, selectedOrderNumber: action.payload };
      case ServiceOrdersActions.view:
        return {
          ...state,
          isEditing: false,
          selectedOrderNumber: '',
          selectedProduct: '',
          closeAllActive: !state.closeAllActive,
        };
      case ServiceOrdersActions.expandCollapseActive:
        return { ...state, isExpandActive: action.payload, closeAllActive: !state.closeAllActive };
      case ServiceOrdersActions.expandCollapseInActive:
        return { ...state, isExpandInActive: action.payload, closeAllInActive: !state.closeAllInActive };
      case ServiceOrdersActions.onInactiveToggleChange:
        return { ...state, isInactiveToggle: !state.isInactiveToggle };
      case 'OnExpandChangeActive':
        return { ...state, closeAllActive: false };
      case 'OnExpandChangeInActive':
        return { ...state, closeAllInActive: false };
      case 'OnChangingServiceOrderId':
        return { ...state, selectedProduct: action.payload };
      default:
        throw new Error();
    }
  };

  const [orderActionsState, dispatch] = useReducer(reducer, {
    isEditing: false,
    selectedProduct: '',
    selectedOrderNumber: '',
    isExpandActive: false,
    isExpandInActive: false,
    isInactiveToggle: false,
    closeAllActive: false,
    closeAllInActive: false,
  });

  const saveServiceOrdersData = async (): Promise<Order[]> => {
    const token = await getAccessToken();
    return postServiceOrder(
      {
        fileId: decryptedFileId ?? 0,
        accountNumber: fileData?.accountId ?? 0,
        orders: orders,
      },
      token,
    );
  };
  const isProductListValid = productListStatus === 'success' && (productListError?.length ?? 0) === 0;

  const updateServiceOrdersData = async (): Promise<boolean> => {
    const token = await getAccessToken();
    return putServiceOrder(
      {
        fileId: decryptedFileId ?? 0,
        accountNumber: fileData?.accountId ?? 0,
        order: orders?.find((x) => x.orderNumber === selectedOrderNumber) ?? ({} as Order),
      },
      token,
    );
  };

  const {
    execute: executeSaveServiceOrdersData,
    value: saveServiceOrdersDataValue,
    status: saveServiceOrdersDataStatus,
    errors: saveServiceOrdersDataError,
  } = useAsync<Order[]>(saveServiceOrdersData, false);

  const {
    execute: executeUpdateServiceOrdersData,
    value: updateServiceOrdersDataValue,
    status: updateServiceOrdersDataStatus,
    errors: updateServiceOrdersDataError,
  } = useAsync<boolean>(updateServiceOrdersData, false);

  const fetchOrderDocument = async (): Promise<OrderDocument> => {
    const token = await getAccessToken();
    const serviceOrderReferenceId = selectedViewDocument.uri.slice(0, selectedViewDocument.uri.indexOf('-'));
    const payload: ServiceOrderRequest = {
      fileId: decryptedFileId ?? 0,
      serviceOrderReferenceId: serviceOrderReferenceId,
    };
    return getOrderDocument(payload, selectedViewDocument.uri, token);
  };

  const fetchOrderDownloadDocuments = async (): Promise<DownloadDocumentResponse> => {
    const token = await getAccessToken();
    const serviceOrderReferenceId = selectedDownloadDocument[0]?.slice(0, selectedDownloadDocument[0]?.indexOf('-'));
    const downloadFormat = downloadAsPdf ? 'pdf' : 'zip';
    const downloadPayload: DownloadDocumentRequest = {
      fileId: decryptedFileId ?? 0,
      serviceOrderReferenceId: serviceOrderReferenceId ?? '0',
      downloadFormat: downloadFormat,
      URI: selectedDownloadDocument,
    };

    const response = await postServiceOrderDocumentDownload(downloadPayload, token);
    return response;
  };

  const {
    execute: executeGetOrderDownloadDocumentData,
    value: getServiceOrderDownloadDocumentValue,
    status: getServiceOrderDownloadDocumentStatus,
    errors: getServiceOrderDownloadDocumentError,
  } = useAsync<DownloadDocumentResponse>(fetchOrderDownloadDocuments, false);

  const {
    execute: executeGetOrderDocumentData,
    value: getServiceOrderDocumentValue,
    status: getServiceOrderDocumentStatus,
    errors: getServiceOrderDocumentError,
  } = useAsync<any>(fetchOrderDocument, false);

  useEffect(() => {
    if (getServiceOrderDocumentValue?.Content?.length > 0) {
      const orderDocumentBytes = getServiceOrderDocumentValue?.Content;
      let docType = '';

      switch (getServiceOrderDocumentValue.MimeType.toUpperCase()) {
        case 'XML':
          docType = 'application/xml';
          break;
        case 'PDF':
          docType = DocumentType;
          break;
        case 'JPG':
        case 'JPEG':
        case 'PNG':
          docType = 'image/jpeg';
          break;
        case 'GIF':
          docType = 'image/gif';
          break;
        case 'BMP':
          docType = 'image/bmp';
          break;
        case 'TIFF':
        case 'TIF':
          docType = 'image/tiff';
          break;
        case 'DOC':
          docType = 'application/msword';
          break;
        case 'RTF':
          docType = 'application/msword';
          break;
        case 'TXT':
          docType = 'text/plain';
          break;
        case 'PXT':
          docType = pxtDocType;
          break;
        case 'FPX':
          docType = fpxDocType;
          break;
        case 'XLS':
          docType = 'application/vnd.ms-excel';
          break;
        case 'XPS':
          docType = 'application/vnd.ms-xpsdocument';
          break;
        case 'WPS':
          docType = 'application/vnd.ms-works';
          break;
        case 'DOCX':
          docType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          break;
        case 'XLSX':
          docType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'ODT':
          docType = 'application/vnd.oasis.opendocument.formula';
          break;
        case 'WPD':
          docType = 'application/wordperfect';
          break;
        case 'ESP':
          docType = 'application/octet-stream';
          break;
        case 'HTM':
          docType = 'text/html';
          break;
        case 'MSG':
          docType = 'application/vnd.ms-outlook';
          break;
        case 'JSON':
          docType = 'application/json';
          break;
        case 'ZIP':
          docType = zipDocType;
          break;
        default:
          docType = DocumentType;
          break;
      }
      if (getServiceOrderDocumentValue.Description == 'Title Reports / RamQuest XML') {
        docType = 'application/json';
      }
      const documentName = getServiceOrderDocumentValue.Name;
      const safeDocumentName = sanitizeFileName(documentName);
      openDocument(orderDocumentBytes, docType, newWindow, WindowFeatures, safeDocumentName);
    }
  }, [getServiceOrderDocumentValue]);

  useEffect(() => {
    if (getServiceOrderDownloadDocumentValue) {
      // Check if a direct DocumentUrl is present in the response
      const url = getServiceOrderDownloadDocumentValue?.DownloadURL;
      let fileName = 'document'; // Default file name
      if (fileData?.fileNumber) {
        fileName = `${fileData.fileNumber}`;
      }
      const docType = downloadAsPdf ? DocumentType : zipDocType;
      if (url) {
        openURLDocument(url, docType, newWindow, WindowFeatures, fileName);
      } else {
        console.error('Download URL is undefined.');
      }
    }
  }, [getServiceOrderDownloadDocumentValue]);

  useEffect(() => {
    // Remove previous info snackbar if any
    if (infoSnackbarId.current) {
      removeSnackbarMessage(infoSnackbarId.current);
      infoSnackbarId.current = null;
    }
    if (getServiceOrderDownloadDocumentStatus === APIStatus.pending) {
      const id = (Math.random() + 1).toString(36).substring(7);
      infoSnackbarId.current = id;
      addSnackbarMessage({
        id,
        message: ServiceOrderMessage.downloading,
        type: SnackBarMessageType.info,
      });
    }
    if (getServiceOrderDownloadDocumentStatus === APIStatus.success) {
      addSnackbarMessage({
        message: ServiceOrderMessage.downloadSuccess,
        type: SnackBarMessageType.success,
      });
    }
    if (getServiceOrderDownloadDocumentStatus === APIStatus.error) {
      addSnackbarMessage({
        message: ServiceOrderMessage.downloadError,
        type: SnackBarMessageType.error,
      });
    }
  }, [addSnackbarMessage, removeSnackbarMessage, getServiceOrderDownloadDocumentStatus]);

  useEffect(() => {
    setTriggerSaveCall(false);
  }, [saveServiceOrdersDataValue, saveServiceOrdersDataError]);

  useEffect(() => {
    setTriggerUpdateCall(false);
  }, [updateServiceOrdersDataValue, updateServiceOrdersDataError]);

  useEffect(() => {
    if (selectedViewDocument !== '') {
      executeGetOrderDocumentData();
      setSelectedViewDocument('');
    }
  }, [selectedViewDocument]);

  useEffect(() => {
    if (documents.length > 0) {
      updateOrder(orderDetails.randomId, documents, 'userUploadedDocuments', false);
      setDocumentUploadStatus('success');
    }
  }, [documents]);

  useEffect(() => {
    if (selectedDownloadDocument?.length > 0) {
      setSelectedDownloadDocument([]);
      executeGetOrderDownloadDocumentData().then();
    }
  }, [selectedDownloadDocument]);

  useEffect(() => {
    if (saveServiceOrdersDataValue || updateServiceOrdersDataValue) {
      refreshServiceOrderData && refreshServiceOrderData();
    }
  }, [saveServiceOrdersDataValue, updateServiceOrdersDataValue]);

  useEffect(() => {
    if (serviceOrderList) {
      const getactiveOrdersCount = serviceOrderList?.filter(
        (item) =>
          item.productName === OrderInsights ||
          item.orderStatus === ServiceOrderStatus.orderplaced ||
          item.orderStatus === ServiceOrderStatus.orderdelivered ||
          item.orderStatus === ServiceOrderStatus.updateDelivered ||
          item.orderStatus === ServiceOrderStatus.closed ||
          item.orderStatus === ServiceOrderStatus.updateRequested,
      ).length;

      if (getactiveOrdersCount === 0) {
        dispatch({ type: ServiceOrdersActions.onInactiveToggleChange });
      }

      setInitialOrders(serviceOrderList);
      setOrders(serviceOrderList);
      setActiveOrdersCount(getactiveOrdersCount ?? 0);
    }
  }, [serviceOrderList]);

  useEffect(() => {
    // Reset submit/update attempted flags after a server-side error so user can try again
    if (saveServiceOrdersDataStatus === 'error') {
      setSubmitAttempted(false);
    }
    if (updateServiceOrdersDataStatus === 'error') {
      setUpdateAttempted(false);
    }
  }, [saveServiceOrdersDataStatus, updateServiceOrdersDataStatus]);

  useEffect(() => {
    setDisplayError(
      productListStatus === 'error' ||
        saveServiceOrdersDataStatus === 'error' ||
        updateServiceOrdersDataStatus === 'error' ||
        getServiceOrderDocumentStatus === 'error' ||
        getServiceOrderDownloadDocumentStatus === 'error',
    );
  }, [
    productListStatus,
    saveServiceOrdersDataStatus,
    updateServiceOrdersDataStatus,
    getServiceOrderDocumentStatus,
    getServiceOrderDownloadDocumentStatus,
  ]);

  useEffect(() => {
    const errorMessages = saveServiceOrdersDataError?.length
      ? saveServiceOrdersDataError
      : updateServiceOrdersDataError?.length
      ? updateServiceOrdersDataError
      : getServiceOrderDocumentError?.length
      ? getServiceOrderDocumentError
      : getServiceOrderDownloadDocumentError?.length
      ? getServiceOrderDownloadDocumentError
      : [];
    setErrMsgs(errorMessages);
  }, [displayError]);

  useEffect(() => {
    if (displayError && errMsgs.length) {
      errMsgs?.map((err) => {
        addGlobalMsg({
          message: err,
          type: 'error',
        });
      });
    }
  }, [displayError, errMsgs]);

  const emptyServiceOrder: Order = {
    serviceOrderReferenceId: 0,
    orderNumber: '',
    saleAmount: 0,
    productName: '',
    productField: [],
    serviceOrderSummary: [],
    orderStatus: '',
    orderStatusDisplay: '',
    owningOffice: '',
    orderNotificationEmail: '',
    isDisclaimerAccepted: false,
    disclaimerInformation: '',
    fileNotes: [],
    note: '',
    userUploadedDocuments: [],
    documents: [],
    updates: [],
    serviceOrderContact: {} as Contact,
    randomId: randomId(),
    expanded: true,
    status: 'Draft',
    createdDate: '',
    updateType: '',
    isDocumentReady: false,
  };

  useEffect(() => {
    if (isAddNewServiceOrderClicked) {
      addServiceOrder();
      setDraftMode(true);
    }
  }, [isAddNewServiceOrderClicked]);

  const addServiceOrder = () => {
    setActiveOrdersCount((prevCount) => prevCount + 1);
    setOrders([{ ...emptyServiceOrder, randomId: randomId() }]);
  };

  const updateOrder = (
    id: any,
    value: any,
    fields: string,
    isFieldValue: boolean,
    isDocumentDelete?: boolean,
    selectedProduct?: ProductResponse,
  ) => {
    let resetFields = false;
    if (fields === 'productName') {
      resetFields = true;
    }
    const newObj = orders.map((obj) => {
      if (obj.randomId === id) {
        if (resetFields) {
          obj.productName = value;
          obj.note = '';
        }

        if (fields === 'userUploadedDocuments' && !isDocumentDelete) {
          return {
            ...obj,
            productField: obj.productField?.map((field) => ({ ...field })),
            note: obj.note,
            userUploadedDocuments: obj.userUploadedDocuments.concat(value).filter((e) => e.uri == '' || !e.uri),
          };
        } else if (fields === 'userUploadedDocuments' && isDocumentDelete) {
          return {
            ...obj,
            productField: obj.productField?.map((field) => ({ ...field })),
            note: obj.note,
            userUploadedDocuments: value,
          };
        }

        return isFieldValue
          ? {
              ...obj,
              productField: obj.productField?.map((field) => ({
                ...field,
                value: field.name === fields ? value : field.value,
              })),
              note: obj.note,
              userUploadedDocuments: obj.userUploadedDocuments.filter((e) => e.uri == '' || !e.uri),
            }
          : {
              ...obj,
              productField: obj.productField?.map((field) => ({ ...field })),
              note: obj.note,
              [fields]: value,
              userUploadedDocuments: obj.userUploadedDocuments.filter((e) => e.uri == '' || !e.uri),
            };
      }
      return obj;
    });
    setOrders(newObj);
  };

  const addOrderFields = (randomId: string, fields: Field[], notificationEmail: string) => {
    const updatedOrders = orders.map((obj) => {
      if (obj.randomId === randomId) {
        obj.orderNotificationEmail = notificationEmail;
        return {
          ...obj,
          productField: [...fields],
        };
      }
      return obj;
    });

    setOrders(updatedOrders);
  };
  const saveOrders = () => {
    setSubmitAttempted(true);
  };

  const cancelUpdate = () => {
    updateOrder(noteDialog.id, 'Cancel', 'updateType', false);
    setNoteDialog({ show: false, id: undefined, note: '' });
    setTriggerUpdateCall(true);
  };

  const cancelOrder = () => {
    setselectedOrderNumber('');
    orderActionsState.isEditing = false;
    setUpdate(false);
    refreshServiceOrderData && refreshServiceOrderData();
  };

  const handleAccordionAction = (o: Order) => {
    if (
      (o.orderStatus || o.productName !== OrderInsights) &&
      (o.orderStatus === ServiceOrderStatus.orderplaced ||
        (o.orderStatus === ServiceOrderStatus.closed && !o.isDocumentReady))
    ) {
      //Cancel Order
      setNoteDialog({ ...noteDialog, show: true, id: o.randomId });
    }
  };
  function handleViewDocument(document: any) {
    setSelectedViewDocument(document);
  }

  function handleDownloadAsZipPdf(documentsSelected: string[], isPdf: boolean) {
    if (documentsSelected && documentsSelected.length > 0) {
      setSelectedDownloadDocument(documentsSelected);
      setDownloadAsPdf(isPdf);
    } else {
      addSnackbarMessage({
        message: `Must select one document to Download`,
        type: 'error',
        onClose: () => {
          setSelectedDownloadDocument([]);
        },
      });
    }
  }

  const updateCallInProgress = useRef(false);

  useEffect(() => {
    if (triggerSaveCall) {
      executeSaveServiceOrdersData().then();
    }
    if (triggerUpdateCall && !updateCallInProgress.current) {
      updateCallInProgress.current = true;
      executeUpdateServiceOrdersData().then(() => {
        setTriggerUpdateCall(false);
        updateCallInProgress.current = false;
      });
    }
  }, [triggerSaveCall, triggerUpdateCall]);

  const isCreateOrderDisabled = (): boolean => {
    return isCreateServiceOrderButtonDisabled
      ? isCreateServiceOrderButtonDisabled
      : !(orders.filter((element) => element.productName !== '').length > 0 && actionStatus !== 'pending'); //TODO
  };

  function orderTitle(order: Order): string[] {
    if (order.productName === OrderInsights) {
      return [];
    } else {
      return order.updates?.length > 0 && order.updates[order.updates.length - 1].updateType
        ? [
            `Order No: ${order.orderNumber}-${order.updates[order.updates.length - 1].updateType}`,
            `Created: ${order.createdDate}`,
          ]
        : [`Order No: ${order.orderNumber}`, `Created: ${order.createdDate}`];
    }
  }

  function getAccordionProps(order: Order, index: number) {
    const i = index + 1;
    const isClosedAndOrderDelivered = order.orderStatus === ServiceOrderStatus.closed && order.isDocumentReady;

    const commonProps = {
      closeAll:
        (order.orderStatus !== ServiceOrderStatus.cancelled &&
          order.orderStatus !== ServiceOrderStatus.openedInError &&
          orderActionsState.closeAllActive) ||
        ((order.orderStatus === ServiceOrderStatus.cancelled ||
          order.orderStatus === ServiceOrderStatus.openedInError) &&
          orderActionsState.closeAllInActive),
      expanded:
        (order.orderStatus !== ServiceOrderStatus.cancelled &&
          order.orderStatus !== ServiceOrderStatus.openedInError &&
          orderActionsState.isExpandActive) ||
        ((order.orderStatus === ServiceOrderStatus.cancelled ||
          order.orderStatus === ServiceOrderStatus.openedInError) &&
          orderActionsState.isExpandInActive),
      isEditing:
        order.orderNumber === orderActionsState.selectedOrderNumber &&
        orderActionsState.isEditing &&
        !orderActionsState.isView,
      isEditDisabled: orderActionsState.isEditing,
      id: order.orderNumber,
      title: `${order.productName}`,
      type: 'orders',
      icon: 'document',
      className: 'serviceorder-accordion',
      //pdfQAAttribute: `OrderSummary${i}ViewPDF`,
      summaryDocumentURI: '',
      editQAAttribute: `ServiceOrder${i}Edit`,
      pdfQAAttribute: `ServiceOrder${i}ViewPDF`,
      deleteQAAttribute:
        order.orderStatus !== ServiceOrderStatus.orderdelivered &&
        order.orderStatus !== ServiceOrderStatus.updateDelivered &&
        order.orderStatus !== ServiceOrderStatus.updateRequested &&
        !isClosedAndOrderDelivered
          ? `ServiceOrder${i}Cancel`
          : `ServiceOrder${i}RequestUpdate`,
      onExpandChange: (expand: boolean, id: string | number) => {
        if (expand) {
          order.orderStatus === ServiceOrderStatus.closed
            ? dispatch({ type: 'OnExpandChangeActive' })
            : dispatch({ type: 'OnExpandChangeInActive' });
        }
      },
      onCustomLabel:
        order.orderStatus !== ServiceOrderStatus.orderdelivered &&
        order.orderStatus !== ServiceOrderStatus.updateDelivered &&
        order.orderStatus !== ServiceOrderStatus.updateRequested &&
        !isClosedAndOrderDelivered
          ? 'Cancel Order'
          : 'Request Update',
    };

    return order.productName !== OrderInsights
      ? {
          ...commonProps,
          status: order.orderStatusDisplay,
          subtitle:
            order.updates?.length > 0 && order.updates[order.updates.length - 1].updateType
              ? [
                  `Order No: ${order.orderNumber}-${order.updates[order.updates.length - 1].updateType}`,
                  `Created: ${order.createdDate}`,
                ]
              : [`Order No: ${order.orderNumber}`, `Created: ${order.createdDate}`],
          ...(order.orderStatus !== ServiceOrderStatus.cancelled &&
            order.orderStatus !== ServiceOrderStatus.openedInError &&
            !editMode &&
            isFileAmendable && {
              onEdit: (id: number | string) => {
                setEditMode(true);
                setselectedOrderNumber(id);
                const selected = orders.find((o) => o.orderNumber === id);
                if (selected) {
                  setOrders((prev) =>
                    prev.map((o) =>
                      o.orderNumber === selected.orderNumber
                        ? { ...selected, documents: [], userUploadedDocuments: [] }
                        : o,
                    ),
                  );
                  setOrderDetails({
                    ...selected,
                    documents: [],
                    userUploadedDocuments: [],
                  });
                }
                setTriggerSaveCall(false);
                dispatch({ type: ServiceOrdersActions.edit, payload: id });
              },
            }),
          ...(order.orderStatus !== ServiceOrderStatus.cancelled &&
            order.orderStatus !== ServiceOrderStatus.openedInError &&
            !editMode && {
              onPDF: (id: number | string) => {
                const summaryDoc = order.userUploadedDocuments?.filter(
                  (doc) => doc.description == 'Service Order Summary PDF',
                )[0];
                setSelectedViewDocument(summaryDoc);
              },
            }),
          ...(order.orderStatus !== ServiceOrderStatus.cancelled &&
            order.orderStatus !== ServiceOrderStatus.openedInError &&
            !editMode && {
              onMessage: async (id: number | string) => {
                setselectedOrderNumber(id);
                const payload: GetActionLogs = {
                  FileId: decryptedFileId ?? 0,
                  ServiceOrderReferenceId: orders?.find((x) => x.orderNumber === id)?.serviceOrderReferenceId ?? 0,
                };
                setAuditAndConversationPayload(payload);
              },
            }),
          ...(order.orderStatus !== ServiceOrderStatus.cancelled &&
            order.orderStatus !== ServiceOrderStatus.openedInError &&
            isFileAmendable &&
            !editMode && {
              onCustom: (id: number | string) => {
                handleAccordionAction(order);
                setUpdateAttempted(false);
                setselectedOrderNumber(id);
                if (
                  order.orderStatus === ServiceOrderStatus.orderdelivered ||
                  order.orderStatus === ServiceOrderStatus.updateRequested ||
                  order.orderStatus === ServiceOrderStatus.updateDelivered ||
                  isClosedAndOrderDelivered
                ) {
                  setUpdate(true);
                }
              },
            }),
        }
      : {
          ...commonProps,
          ...getOrderInsightTitleAndStatus(order.serviceOrderSummary),
        };
  }

  const getOrderInsightTitleAndStatus = (summaries: OrderInsightsSummary[] = []): InsightTitleStatus => {
    const sortedSummary =
      summaries.length > 0
        ? [...summaries].sort((a, b) => new Date(b.orderedDate).getTime() - new Date(a.orderedDate).getTime())
        : [];

    const OrderDetails =
      sortedSummary.length > 0
        ? [
            `Order No: ${sortedSummary[0].orderNumber}`,
            `Created: ${formatDate(sortedSummary[0].orderedDate, undefined, 'full')}`,
          ]
        : [];

    const OrderStatus = sortedSummary.length > 0 ? sortedSummary[0].orderStatus : '';

    return { subtitle: OrderDetails, status: OrderStatus };
  };

  const renderOrderDetails = (orders: Order[] = []) =>
    orders.map((order, index) => {
      const isEditingOrder = editMode && order.orderNumber === orderActionsState.selectedOrderNumber;
      // only draft mode fully enabled; in edit mode all controls are disabled except those in editableFields
      const contentDisabled = draftMode ? false : true;
      const editableFields = isEditingOrder ? ['orderNotificationEmail', 'userUploadedDocuments'] : [];

      return order.productName !== OrderInsights ? (
        // deepcode ignore ReactXssDomContentSafe: Props are passed to React component which auto-escapes text content
        <AccordionContent key={order.randomId} {...getAccordionProps(order, index)}>
          <ServiceOrderForm
            index={index + 1}
            order={order}
            productList={productList ?? []}
            contentDisabled={contentDisabled}
            editableFields={editableFields}
            updateOrder={updateOrder}
            addOrderFields={addOrderFields}
            submitAttempted={submitAttempted}
            setSubmitAttempted={setSubmitAttempted}
            updateAttempted={updateAttempted}
            setUpdateAttempted={setUpdateAttempted}
            setTriggerSaveCall={setTriggerSaveCall}
            setTriggerUpdateCall={setTriggerUpdateCall}
            setOnViewDocument={handleViewDocument}
            setOnDownloadPdfZip={handleDownloadAsZipPdf}
          />
        </AccordionContent>
      ) : (
        // deepcode ignore ReactXssDomContentSafe: Props are passed to React component which auto-escapes text content
        <AccordionContent key={order.productName + [index]} {...getAccordionProps(order, index)}>
          <OrderInsightHistory index={order.randomId} orderSummary={order.serviceOrderSummary}></OrderInsightHistory>
        </AccordionContent>
      );
    });

  const activeOrders = orders?.filter(
    (item) =>
      item.productName === OrderInsights ||
      item.orderStatus === ServiceOrderStatus.orderplaced ||
      item.orderStatus === ServiceOrderStatus.orderdelivered ||
      item.orderStatus === ServiceOrderStatus.updateRequested ||
      item.orderStatus === ServiceOrderStatus.closed ||
      item.orderStatus === ServiceOrderStatus.updateDelivered,
  );

  const inactiveOrders = orders?.filter(
    (item) =>
      item.orderStatus === ServiceOrderStatus.cancelled || item.orderStatus === ServiceOrderStatus.openedInError,
  );

  const selectableProducts = (productList ?? []).filter((p) => p.productName !== OrderInsights);

  const showEmptyPage =
    ((productListError?.length ?? 0) > 0 || !selectableProducts.length) &&
    (isAddNewServiceOrderClicked || serviceOrderList?.length === 0);

  const actionStatus =
    productListStatus === 'pending' ||
    saveServiceOrdersDataStatus === 'pending' ||
    updateServiceOrdersDataStatus === 'pending' ||
    getServiceOrderDocumentStatus === 'pending' ||
    getServiceOrderDownloadDocumentStatus === 'pending'
      ? 'pending'
      : 'idle';

  // only show the one being edited when in editMode
  const editedOrders = orders.filter((o) => o.orderNumber === orderActionsState.selectedOrderNumber);
  const ordersToDisplay = editMode ? editedOrders : activeOrders;

  if (showEmptyPage) {
    return (
      <Grid container spacing={2}>
        <div className={classes.contentClass}>
          <NoResults
            showButton={false}
            type="empty-box-state"
            titleText={`Streamline Your Title Process`}
            pText={
              '<p>First American offers a wide range of title search products designed to support your real estate transactions. With this feature enabled, you can order, review, cancel, and update your title service requests with ease – all in one place. By leveraging our expertise in data transformation and complementary services, you’ll ensure smooth transactions backed by accurate, comprehensive information.</p>' +
              '<p>Ready to start ordering title search products from First American? Connect with your Sales Representative to get started.</p>'
            }
          />
        </div>
      </Grid>
    );
  }

  return (
    <>
      <LoadingSpinner status={actionStatus} variant="linear" />
      {!isAddNewServiceOrderClicked && initialOrders ? (
        <div>
          <div className="paper-layout">
            <Container>
              <PageHeader
                title={draftMode ? 'Create Service Order' : 'Service Orders'}
                subtitle={
                  draftMode
                    ? 'Service Orders is designed to provide a communication portal for title agents to order, track and receive title products from First American. When placing your order, ensure all required details are provided to assist the production team with fulfilling your order. Please Note: Information shown on this page is for this Service Order only and will not affect information stored elsewhere in the system.'
                    : 'View, edit, or cancel newly placed service orders. For delivered products, expand the section to view notes and documents or download them. Request an update service (Amend, Datedown or Update) on delivered products or add a new service order.'
                }
                id={`ServiceOrder${fileData?.propertyState}`}
                contentRight={
                  selectableProducts.length > 0 && (
                    <AgentNetButton
                      variant="contained"
                      color="primary"
                      onClick={handleAddNewServiceOrder}
                      disabled={fileData?.fileStatus != openFile}
                      data-testid="ServiceOrderCreateNew"
                      plusIcon
                      size="small"
                    >
                      Add Service Order
                    </AgentNetButton>
                  )
                }
                disableMargin="x"
                menuItems={[
                  {
                    label: 'Knowledge Base',
                    link: KBLinks.serviceOrderFiles,
                  },
                ]}
              />
              {activeOrdersCount === 0 && (
                <Notification inline severity="warning" className={classes.warningMsg}>
                  {noActiveServiceOrders}
                </Notification>
              )}

              {editMode && (
                <Notification inline severity="warning" className={classes.warningMsg}>
                  {serviceOrderEditMessage}
                </Notification>
              )}
              {/* use filtered list when editing */}
              {renderOrderDetails(ordersToDisplay)}

              {/* hide inactive section when editing */}
              {!editMode && inactiveOrders?.length ? (
                <AgentNetDivider
                  title="Inactive Service Orders"
                  {...(orderActionsState.isInactiveToggle && {
                    buttonName: 'Close All',
                    buttonName2: 'Expand All',
                  })}
                  onClick={() => {
                    dispatch({ type: ServiceOrdersActions.expandCollapseInActive, payload: false });
                  }}
                  onClick2={() => {
                    dispatch({ type: ServiceOrdersActions.expandCollapseInActive, payload: true });
                  }}
                  disablePaddingX
                  onSwitch={() => {
                    dispatch({ type: ServiceOrdersActions.onInactiveToggleChange });
                  }}
                  switchValue={orderActionsState.isInactiveToggle}
                />
              ) : null}
              {!editMode && orderActionsState.isInactiveToggle ? renderOrderDetails(inactiveOrders) : null}
            </Container>
          </div>
        </div>
      ) : (
        ''
      )}
      {isAddNewServiceOrderClicked && orders.length && isProductListValid ? (
        <PageHeader
          title={draftMode ? 'Create Service Order' : 'Order Details'}
          subtitle={
            'Service Orders is designed to provide a communication portal for title agents to order, track and receive title products from First American. When placing your order, ensure all required details are provided to assist the production team with fulfilling your order. Please Note: Information shown on this page is for this Service Order only and will not affect information stored elsewhere in the system.'
          }
          menuItems={[
            {
              label: 'Knowledge Base',
              link: KBLinks.serviceOrderFiles,
            },
          ]}
        />
      ) : (
        ''
      )}

      <div className={classes.serviceOrdersWrap}>
        {drawerOpen && (
          <>
            <h6>Edit</h6>
            <EditOrderForm
              order={orders.find((x) => x.orderNumber === selectedOrderNumber) ?? ({} as Order)}
              updateOrder={updateOrder}
              setTriggerUpdateCall={setTriggerUpdateCall}
              updateAttempted={updateAttempted}
              setUpdateAttempted={setUpdateAttempted}
              handleDrawerOpen={drawerOpen}
              onDismissAction={() => setDrawerOpen(false)}
              onSecondaryAction={() => setDrawerOpen(false)}
              isEdit={drawerOpen ?? false}
            />
          </>
        )}

        {update && (
          <EditOrderForm
            order={orders.find((x) => x.orderNumber === selectedOrderNumber) ?? ({} as Order)}
            updateOrder={updateOrder}
            setTriggerUpdateCall={setTriggerUpdateCall}
            updateAttempted={updateAttempted}
            setUpdateAttempted={setUpdateAttempted}
            handleDrawerOpen={update}
            onDismissAction={() => setUpdate(false)}
            onSecondaryAction={() => setUpdate(false)}
            isEdit={drawerOpen ?? false}
          />
        )}

        {auditAndConversationPayload ? (
          <ServiceOrderDrawer
            isDashboardMessagePanelOpen={false}
            serviceOrderPayload={auditAndConversationPayload}
            serviceOrderNumber={selectedOrderNumber}
            onClose={() => {
              setAuditAndConversationPayload(undefined);
            }}
          />
        ) : (
          ''
        )}
        {showEmptyPage ? (
          <Grid container spacing={2}>
            <>
              <Grid item xs={12}>
                <PageHeader
                  divider={true}
                  menuItems={[
                    {
                      label: 'Knowledge Base',
                      link: KBLinks.serviceOrderDashboard,
                    },
                  ]}
                  title="Service Orders"
                />
              </Grid>
              <div className={classes.contentClass}>
                <NoResults
                  showButton={false}
                  type="empty-box-state"
                  titleText={`Streamline Your Title Process`}
                  pText={
                    '<p>First American offers a wide range of title search products designed to support your real estate transactions. With this feature enabled, you can order, review, cancel, and update your title service requests with ease – all in one place. By leveraging our expertise in data transformation and complementary services, you’ll ensure smooth transactions backed by accurate, comprehensive information.</p>' +
                    '<p>Ready to start ordering title search products from First American? Connect with your Sales Representative to get started.</p>'
                  }
                />
              </div>
            </>
          </Grid>
        ) : (
          <>
            {orders.length && isProductListValid ? (
              draftMode ? (
                <>
                  {orders
                    .filter((j) => j.status === 'Draft')
                    .map((p, i) => (
                      <ServiceOrderForm
                        index={i + 1}
                        key={p.randomId}
                        order={p}
                        productList={productList ?? []}
                        contentDisabled={!draftMode}
                        updateOrder={updateOrder}
                        addOrderFields={addOrderFields}
                        submitAttempted={submitAttempted}
                        setSubmitAttempted={setSubmitAttempted}
                        updateAttempted={updateAttempted}
                        setUpdateAttempted={setUpdateAttempted}
                        setTriggerSaveCall={setTriggerSaveCall}
                        setTriggerUpdateCall={setTriggerUpdateCall}
                        setOnViewDocument={handleViewDocument}
                        setOnDownloadPdfZip={handleDownloadAsZipPdf}
                      />
                    ))}
                </>
              ) : (
                <div>
                  <AgentNetConfirmationDialog
                    onConfirm={cancelUpdate}
                    onDismissAction={() => setNoteDialog({ show: false, id: undefined })}
                    open={noteDialog.show}
                    size="md"
                    disablePrimaryBtn={noteDialog?.note?.trim().length > 0 ? false : true}
                    dialogTitle={'Cancel Order'}
                    qaAttrPrefix="ServiceOrderCancel"
                    dialogBtnContent="Confirm Cancellation"
                    dialogTextHTML={
                      <>
                        <Typography variant="body2" className={classes.mb2}>
                          Please leave your reason for cancelling this service order.
                        </Typography>
                        <AgentNetTextInput
                          variant="outlined"
                          label="Note"
                          multiline
                          fullWidth
                          value={noteDialog.note}
                          required
                          onChange={(e: any) => {
                            setNoteDialog({ ...noteDialog, note: e.target.value });
                            updateOrder(noteDialog.id, e.target.value, 'note', false);
                          }}
                          data-testid={'ServiceOrderCancelNotes'}
                          rows={4}
                        />
                      </>
                    }
                  />
                </div>
              )
            ) : (
              ''
            )}
          </>
        )}

        {/* actions for both Create and Edit */}
        {(isAddNewServiceOrderClicked || orderActionsState.isEditing) && (
          <Grid container justify="flex-end" style={{ marginTop: 16 }}>
            <ServiceOrderActions
              hasServiceOrders={!isAddNewServiceOrderClicked}
              saveServiceOrder={() => {
                if (isAddNewServiceOrderClicked && !orderActionsState.isEditing) {
                  setSubmitAttempted(true);
                } else if (orderActionsState.isEditing) {
                  setUpdateAttempted(true);
                } else {
                  return '';
                }
              }}
              cancelServiceOrder={() => {
                cancelOrder();
                dispatch({ type: ServiceOrdersActions.view });
              }}
              isSaveDisabled={
                actionStatus === 'pending' || (isAddNewServiceOrderClicked ? isCreateOrderDisabled() : false)
              }
            />
          </Grid>
        )}
      </div>
    </>
  );
};

export default ServiceOrderFC;
